import React from 'react';
import { Container, Typography, Alert, Button, Stack, Card, CardContent, RadioGroup, FormControlLabel, Radio, LinearProgress, Box, ButtonBase } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const TestPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [loading, setLoading] = React.useState<boolean>(!!token);
  const [valid, setValid] = React.useState<boolean>(false);
  const [err, setErr] = React.useState<string | null>(null);
  const [attemptId, setAttemptId] = React.useState<number | null>(null);
  const [questions, setQuestions] = React.useState<any[]>([]);
  const [idx, setIdx] = React.useState<number>(0);
  const [selected, setSelected] = React.useState<string>('');
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [finished, setFinished] = React.useState<boolean>(false);
  const [phase, setPhase] = React.useState<'IQ'|'TRANSITION'|'STRESS'|'DONE'>('IQ');
  const [stressAttemptId, setStressAttemptId] = React.useState<number | null>(null);
  const [iqTotal, setIqTotal] = React.useState<number>(0);
  const [stressTotal, setStressTotal] = React.useState<number | null>(null);
  const [score, setScore] = React.useState<number | null>(null);
  const [preview, setPreview] = React.useState<boolean>(false);
  const [timeLeft, setTimeLeft] = React.useState<number | null>(null);

  // Reset timera na zmianę pytania
  React.useEffect(() => {
    if (!questions.length || finished || phase === 'TRANSITION') return;
    const sec = Number(questions[idx]?.time_limit_sec || 60);
    setTimeLeft(sec);
  }, [idx, questions, finished, phase]);

  // Odliczanie czasu
  React.useEffect(() => {
    if (finished || !questions.length) return;
    if (timeLeft == null || timeLeft <= 0) return;
    const t = setTimeout(() => setTimeLeft((s)=> (s == null ? null : s-1)), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, finished, questions.length]);

  // Auto-przejście po czasie
  React.useEffect(() => {
    if (finished || !questions.length || phase === 'TRANSITION') return;
    if (timeLeft !== 0) return;
    if (questions.length > 0) {
      // brak wyboru traktuj jako brak punktu
      const proceed = async () => {
        try{
          setSubmitting(true);
          if (phase === 'IQ' && !preview && attemptId) {
            await api.post('/recruit/public/test/answer', { attempt_id: attemptId, question_id: questions[idx].id, selected_option_key: selected || 'X', response_time_ms: (questions[idx]?.time_limit_sec||60)*1000 });
          } else if (phase === 'STRESS' && stressAttemptId) {
            await api.post('/recruit/public/stress/answer', { attempt_id: stressAttemptId, question_id: questions[idx].id, selected_option_key: selected || 'X' });
          } else if (preview) {
            const ok = questions[idx]?.correct_option_key && questions[idx].correct_option_key === selected;
            if (ok) setScore((s)=> (s||0)+1);
          }
        } catch {}
        finally {
          setSubmitting(false);
          setSelected('');
          if (idx < questions.length-1) setIdx(v=>v+1); else {
            if (phase === 'IQ') {
              if (!preview && attemptId) { try { await api.post('/recruit/public/test/finish', { attempt_id: attemptId }); } catch {} }
              setPhase('TRANSITION');
              setQuestions([]);
              setTimeLeft(null);
            } else if (phase === 'STRESS') {
              if (stressAttemptId) { try { await api.post('/recruit/public/stress/finish', { attempt_id: stressAttemptId }); } catch {} }
              setPhase('DONE');
              setFinished(true);
            }
          }
        }
      };
      proceed();
    }
  }, [timeLeft, finished, questions, idx, selected, preview, attemptId, phase, stressAttemptId, token]);

  React.useEffect(() => {
    let mounted = true;
    if (!token) return;
    (async () => {
      try {
        const resp = await api.get('/recruit/public/test/verify', { params: { token } });
        if (!mounted) return;
        setValid(Boolean(resp.data?.success));
      } catch (e: any) {
        setErr(e?.response?.data?.message || 'Token nieprawidłowy lub wygasł');
      } finally {
        setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  return (
    <Container maxWidth="lg" sx={{ py: 6, bgcolor: '#FAFAFA' }}>
      <Box sx={{ display:'flex', justifyContent:'center' }}>
        <Typography
          component="span"
          sx={{
            fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
            fontWeight: 600,
            fontSize: '21.94px',
            lineHeight: '26px',
            letterSpacing: '0.12em',
            color: 'rgba(197, 48, 48, 1)',
            width: '140px',
            height: '26px',
            display: 'inline-block',
            textAlign: 'center'
          }}
        >
          UNITRESCO
        </Typography>
      </Box>
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial',
          fontWeight: 600,
          fontSize: '48px',
          lineHeight: '100%',
          letterSpacing: 0,
          color: 'rgba(2, 2, 66, 1)',
          textAlign: 'center',
          mt: '75px',
          mb: 3
        }}
      >
        Test online
      </Typography>
      {!token && questions.length === 0 && !finished && (
        <Stack spacing={2} sx={{ width: 792, maxWidth: '100%', mx: 'auto', textAlign: 'center', px: 2 }}>
          <Alert severity="info">To jest strona testowa dostępna bez tokenu. Gdy integracja będzie gotowa, adres zadziała też jako /test?token=...</Alert>
          <Typography sx={{
            fontFamily: 'Roboto', fontWeight: 400, fontSize: { xs: '16px', sm: '18px' }, lineHeight: '150%', letterSpacing: 0,
            color: 'rgba(2, 2, 66, 1)', width: { xs: '100%', sm: 620 }, mx: 'auto', wordBreak: 'break-word', overflowWrap: 'anywhere'
          }}>
            Witamy w teście kompetencji.
            <br />
            Ten test składa się z 25 pytań przeznaczonych do oceny Twoich umiejętności analitycznych, logicznego myślenia oraz zdolności rozwiązywania problemów. Każde pytanie ma określony czas na odpowiedź. Test zajmie do 35 min.
          </Typography>
          <Button variant="contained" onClick={async()=>{
            try{
              setErr(null);
              setScore(0);
              const resp = await api.post('/recruit/public/test/start', { token: '' });
              if (resp.data?.success) {
                setAttemptId(resp.data.data.attempt_id); // w preview będzie null
                setQuestions(resp.data.data.questions || []);
                setPreview(Boolean(resp.data?.data?.preview));
                try { setIqTotal((resp.data?.data?.questions || []).length || 0); } catch {}
                setIdx(0);
                setSelected('');
                const firstSec = Number((resp.data?.data?.questions?.[0]?.time_limit_sec) || 60);
                setTimeLeft(firstSec);
              } else {
                throw new Error(resp.data?.message || 'Nie udało się rozpocząć testu (podgląd)');
              }
            } catch(e:any){ setErr(e?.response?.data?.message || e?.message || 'Błąd startu (podgląd)'); }
          }} sx={{
            minWidth: '129px', height: '40px', borderRadius: '4px', px: '12px', py: '8px',
            bgcolor: 'rgba(63, 63, 220, 1)', '&:hover': { bgcolor: 'rgba(63, 63, 220, 0.9)' },
            textTransform: 'none', fontFamily: 'Roboto', fontWeight: 500, fontSize: '14px', lineHeight: '16px', color: 'rgba(255, 255, 255, 1)'
          }}>Rozpocznij test (podgląd)</Button>
        </Stack>
      )}
      {token && (
        <Stack spacing={2} sx={{ width: 920, maxWidth: '100%', mx: 'auto', px: 2 }}>
          {loading && <Typography>Sprawdzam token…</Typography>}
          {!loading && !valid && <Alert severity="error">{err || 'Token nieprawidłowy lub wygasł'}</Alert>}
          {!loading && valid && !attemptId && (
            <>
              <Box sx={{ width: 920, maxWidth: '100%', mx: 'auto', p: 3, border: '1px solid rgba(2, 2, 66, 0.12)', borderRadius: '8px', bgcolor: '#fff', textAlign: 'center' }}>
                <Typography sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: { xs: '16px', sm: '18px' },
                  lineHeight: '150%',
                  letterSpacing: 0,
                  color: 'rgba(2, 2, 66, 1)',
                  width: { xs: '100%', sm: 620 },
                  mx: 'auto',
                  wordBreak: 'break-word', overflowWrap: 'anywhere',
                  mb: 2
                }}>
                  Witamy w teście kompetencji.
                  <br />
                  Ten test składa się z 25 pytań przeznaczonych do oceny Twoich umiejętności analitycznych, logicznego myślenia oraz zdolności rozwiązywania problemów. Każde pytanie ma określony czas na odpowiedź. Test zajmie do 35 min.
                </Typography>
                <Button variant="contained" onClick={async()=>{
                try {
                  setErr(null);
                  const resp = await api.post('/recruit/public/test/start', { token });
                  if (resp.data?.success) {
                    setAttemptId(resp.data.data.attempt_id);
                    setQuestions(resp.data.data.questions || []);
                    setPreview(Boolean(resp.data?.data?.preview));
                    try { setIqTotal((resp.data?.data?.questions || []).length || 0); } catch {}
                    setIdx(0);
                    setSelected('');
                    const firstSec = Number((resp.data?.data?.questions?.[0]?.time_limit_sec) || 60);
                    setTimeLeft(firstSec);
                  } else {
                    throw new Error(resp.data?.message || 'Nie udało się rozpocząć testu');
                  }
                } catch (e: any) {
                  setErr(e?.response?.data?.message || e?.message || 'Błąd startu testu');
                }
              }} sx={{
                minWidth: '129px',
                height: '40px',
                borderRadius: '4px',
                px: '12px',
                py: '8px',
                bgcolor: 'rgba(63, 63, 220, 1)',
                '&:hover': { bgcolor: 'rgba(63, 63, 220, 0.9)' },
                textTransform: 'none',
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '16px',
                color: 'rgba(255, 255, 255, 1)'
              }}>Rozpocznij test</Button>
              </Box>
            </>
          )}

          {!loading && (token ? valid : true) && (attemptId !== null || preview) && !finished && questions.length > 0 && (
            <>
              {/* New styled question card */}
              <Box sx={{ width: 920, maxWidth: '100%', mx: 'auto', p: 3, border: '1px solid rgba(2, 2, 66, 0.12)', borderRadius: '8px', bgcolor: '#fff' }}>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 2 }}>
                  <Typography sx={{ color: 'text.secondary' }}>
                    {(() => {
                      const total = (phase === 'IQ') ? (iqTotal + (stressTotal ?? 5)) : ((iqTotal) + (stressTotal ?? (questions.length || 0)));
                      const current = (phase === 'IQ') ? (idx+1) : (iqTotal + idx + 1);
                      return `Pytanie ${current}/${total}`;
                    })()}
                  </Typography>
                  <Box sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'rgba(244, 238, 255, 0.8)', color: 'rgba(2, 2, 66, 1)', fontWeight: 600 }}>Pozostały czas: {timeLeft}s</Box>
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: 16, color: 'rgba(2, 2, 66, 1)', mb: 1.5 }}>{questions[idx]?.text}</Typography>
                {questions[idx]?.image_url && (
                  <Box sx={{ my: 1 }}>
                    <img src={questions[idx].image_url} alt="question" style={{ maxWidth:'100%', borderRadius:6 }} />
                  </Box>
                )}
                <Box>
                  {(questions[idx]?.options||[]).map((o:any)=> {
                    const isSel = selected === o.option_key;
                    return (
                      <ButtonBase key={o.option_key} onClick={()=> setSelected(o.option_key)} sx={{ display:'block', textAlign:'left', width: '100%', borderRadius: '6px', border: '1px solid', borderColor: isSel ? 'rgba(63, 63, 220, 1)' : 'rgba(2, 2, 66, 0.16)', boxShadow: isSel ? '0 0 0 3px rgba(63, 63, 220, 0.15)' : 'none', p: 1.25, mb: 1.25 }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                          <Radio size="small" checked={isSel} value={o.option_key} sx={{ color: 'rgba(89, 89, 102, 1)', '&.Mui-checked': { color: 'rgba(89, 89, 102, 1)' } }} />
                          <Typography>{o.option_text || o.option_key}</Typography>
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Box>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                  {idx < questions.length-1 ? (
                    <Button variant="contained" disabled={!selected || submitting} onClick={async()=>{
                      try{
                        setSubmitting(true);
                        if (!preview && phase === 'IQ') {
                          await api.post('/recruit/public/test/answer', { attempt_id: attemptId, question_id: questions[idx].id, selected_option_key: selected });
                        } else if (phase === 'STRESS' && stressAttemptId) {
                          await api.post('/recruit/public/stress/answer', { attempt_id: stressAttemptId, question_id: questions[idx].id, selected_option_key: selected });
                        } else {
                          const ok = questions[idx]?.correct_option_key && questions[idx].correct_option_key === selected;
                          if (ok) setScore((s)=> (s||0)+1);
                        }
                        setSelected('');
                        setIdx(v=>Math.min(questions.length-1, v+1));
                      }catch(e:any){ setErr(e?.response?.data?.message || e?.message || 'Błąd zapisu odpowiedzi'); }
                      finally{ setSubmitting(false); }
                    }} sx={{
                      minWidth: '64px',
                      height: '40px',
                      borderRadius: '4px',
                      px: '12px',
                      py: '8px',
                      bgcolor: 'rgba(63, 63, 220, 1)',
                      '&:hover': { bgcolor: 'rgba(63, 63, 220, 0.9)' },
                      textTransform: 'none',
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '16px',
                      color: 'rgba(255, 255, 255, 1)'
                    }}>Dalej</Button>
                  ) : (
                    <Button variant="contained" disabled={!selected || submitting} onClick={async()=>{
                      try{
                        setSubmitting(true);
                        if (!preview && phase === 'IQ') {
                          await api.post('/recruit/public/test/answer', { attempt_id: attemptId, question_id: questions[idx].id, selected_option_key: selected });
                          try { await api.post('/recruit/public/test/finish', { attempt_id: attemptId }); } catch {}
                          setPhase('TRANSITION');
                          setQuestions([]);
                          setTimeLeft(null);
                          return;
                        } else if (phase === 'STRESS' && stressAttemptId) {
                          await api.post('/recruit/public/stress/answer', { attempt_id: stressAttemptId, question_id: questions[idx].id, selected_option_key: selected });
                          try { await api.post('/recruit/public/stress/finish', { attempt_id: stressAttemptId }); } catch {}
                          setPhase('DONE');
                          setFinished(true);
                        } else {
                          const ok = questions[idx]?.correct_option_key && questions[idx].correct_option_key === selected;
                          const localScore = (ok ? (score||0)+1 : (score||0));
                          setScore(localScore);
                          // In preview skip stress
                          setPhase('TRANSITION');
                        }
                      }catch(e:any){ setErr(e?.response?.data?.message || e?.message || 'Błąd zakończenia testu'); }
                      finally{ setSubmitting(false); }
                    }} sx={{
                      minWidth: '64px',
                      height: '40px',
                      borderRadius: '4px',
                      px: '12px',
                      py: '8px',
                      bgcolor: 'rgba(63, 63, 220, 1)',
                      '&:hover': { bgcolor: 'rgba(63, 63, 220, 0.9)' },
                      textTransform: 'none',
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '16px',
                      color: 'rgba(255, 255, 255, 1)'
                    }}>{phase === 'IQ' ? 'Dalej' : 'Zakończ test'}</Button>
                  )}
                </Stack>
              </Box>
            </>
          )}

          {phase === 'TRANSITION' && !finished && (
            <Box sx={{
              width: 920, maxWidth:'100%', mx: 'auto', p: 3, border: '1px solid rgba(2, 2, 66, 0.12)', borderRadius: '8px', bgcolor: '#fff', textAlign:'center'
            }}>
              <Typography sx={{ fontFamily:'Roboto', fontWeight:700, color:'rgba(2, 2, 66, 1)', mb: 1 }}>Część 2: szybkie decyzje</Typography>
              <Typography sx={{ fontFamily:'Roboto', fontWeight:400, fontSize:{ xs:'16px', sm:'18px' }, lineHeight:'150%', color:'rgba(2, 2, 66, 1)', mb: 2 }}>
                Teraz przejdziesz do krótkiego testu odporności na stres (5 pytań).
                Czas na odpowiedź będzie krótszy — około 10–20 sekund na pytanie.
              </Typography>
              <Button variant="contained" onClick={async()=>{
                try{
                  setErr(null);
                  const resp = await api.post('/recruit/public/stress/start', { iq_attempt_id: attemptId, token });
                  if (resp.data?.success) {
                    setPhase('STRESS');
                    setStressAttemptId(resp.data?.data?.attempt_id || null);
                    const qs = resp.data?.data?.questions || [];
                    setQuestions(qs);
                    setStressTotal(qs.length || 0);
                    setIdx(0);
                    setSelected('');
                    const firstSec = Number((qs?.[0]?.time_limit_sec) || 10);
                    setTimeLeft(firstSec);
                  }
                } catch (e:any) {
                  setErr(e?.response?.data?.message || e?.message || 'Błąd startu części 2');
                }
              }} sx={{
                minWidth:'160px', height:'40px', borderRadius:'4px', px:'12px', py:'8px',
                bgcolor:'rgba(63, 63, 220, 1)', '&:hover':{ bgcolor:'rgba(63, 63, 220, 0.9)' },
                textTransform:'none', fontFamily:'Roboto', fontWeight:500, fontSize:'14px', color:'#fff'
              }}>Rozpocznij część 2</Button>
            </Box>
          )}

          {finished && (
            <Box sx={{ width: 920, maxWidth:'100%', mx: 'auto', p: 3, border: '1px solid rgba(2, 2, 66, 0.12)', borderRadius: '8px', bgcolor: '#fff' }}>
              <Box sx={{ width: 792, height: 432, mx: 'auto', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap: 6 }}>
                <Box sx={{ width: 120, height: 120, borderRadius: '100px', bgcolor: 'rgba(224, 255, 238, 1)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'rgba(2, 2, 66, 1)' }} />
                </Box>
                <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: 'rgba(2, 2, 66, 1)' }}>Ukończyłeś test</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(2, 2, 66, 1)', textAlign:'center' }}>Dziękujemy za udział. Skontaktujemy się z Tobą wkrótce</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </Container>
  );
};

export default TestPage;


